import React, { useMemo, useState, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import Fuse from 'fuse.js';
import { categoriesData } from '../../helpers/categories';
import { Category } from '../../model/model';
import { demoState } from '../../states/DemoState';
import { useRecoilValue } from 'recoil';
import { PlayArrow } from '@mui/icons-material';

const AutocompleteContainer = styled.div`
    position: relative;
    width: fit-content;
    background: #FFFFFF;
`;

const InputWrapper = styled.div`
    position: relative;
    width: fit-content;
`;

const RotatingPlayArrow = styled(PlayArrow) <{ $isExpanded: boolean }>`
    position: absolute;
    right: 20px;
    top: 60%;
    transform: ${({ $isExpanded }) => $isExpanded ? 'translateY(-50%) rotate(90deg)' : 'translateY(-50%) rotate(0)'};
    transition: transform 0.3s ease !important;
    cursor: pointer;
`;

const disabledStyle = css`
    background-color: #fbd1d9;
    pointer-events: none;
`;

const AutocompleteInput = styled.input<{ isDisabled?: boolean }>`
    width: 21vw;
    padding: 16px 24px;
    padding-right: 40px; // Make space for the arrow button
    margin-top: 1rem;
    font-size: 0.9rem;
    border: 2px solid #58585A;
    border-radius: 30px;
    outline: none;
    background: transparent;
    text-align: center;

    ${({ isDisabled }) => isDisabled && disabledStyle}
`;

const DropdownList = styled.ul`
    position: absolute;
    width: 100%;
    max-height: 200px;
    overflow-y: auto;
    list-style-type: none;
    margin: 0;
    padding: 0;
    border: 2px solid #58585A;
    border-radius: 5px;
    background: #fff;
    z-index: 2;
`;

const ListItem = styled.li`
    padding: 8px 12px;
    cursor: pointer;
    &:hover {
        background-color: #e5e5e5;
    }
`;

const fuseOptions = {
    keys: ['segment', 'categoryPath'],
    threshold: 0.3,
    shouldSort: true,
};

const fuse = new Fuse(categoriesData, fuseOptions);

interface Props {
    category: Category;
    onChange?: (category: Category) => void;
}

const CategoriesList: React.FC<Props> = ({ category, onChange }) => {
    const [inputValue, setInputValue] = useState<string | null>(category ? category.segment : null);
    const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
    const [isExpanded, setIsExpanded] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const demo = useRecoilValue(demoState);

    const searchCategories = (categoryString: string | null) => {
        if (!categoryString) {
            setFilteredCategories([]);
            setIsExpanded(false);
            return;
        }

        const idPattern = /^\(?\d+\)?$/; // This regex matches strings like "12345" or "(12345)"
        if (idPattern.test(categoryString)) {
            const strippedID = categoryString.replace(/[()]/g, '');
            const idMatch = categoriesData.filter(category =>
                category.hierarchy.includes(`(${strippedID})`)
            );
            setFilteredCategories(idMatch);
        } else {
            // Use Fuse for non-ID queries
            const results = fuse.search(categoryString);
            const categories = results.map(result => ({
                segment: result.item.segment,
                hierarchy: result.item.hierarchy,
                comissionGroupOld: result.item.comissionGroupOld,
                comissionGroupNew: result.item.comissionGroupNew,
            }));
            setFilteredCategories(categories);
        }
        setIsExpanded(true);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsExpanded(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleInputActualChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            searchCategories(inputValue);
        }
    }

    const handleCategoryClick = (category: Category) => {
        setInputValue(category.segment);
        setFilteredCategories([]);
        if (onChange) {
            onChange(category);
        }
    }

    const memoizedListItems = useMemo(() => {
        return filteredCategories.map(category => (
            <ListItem key={category.hierarchy} onClick={() => handleCategoryClick(category)}>
                {category.hierarchy}
            </ListItem>
        ));
    }, [filteredCategories, handleCategoryClick]);

    return (
        <AutocompleteContainer ref={containerRef}>
            <InputWrapper>
                <AutocompleteInput
                    value={inputValue ? inputValue : ""}
                    onChange={handleInputActualChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Wybierz kategorie..."
                    onClick={(e: React.MouseEvent<HTMLInputElement>) => e.currentTarget.select()}
                    isDisabled={demo}
                />
                <RotatingPlayArrow fontSize='medium' $isExpanded={isExpanded} onClick={() => setIsExpanded(!isExpanded)} />
            </InputWrapper>
            {isExpanded && filteredCategories.length > 0 && (
                <DropdownList>
                    {memoizedListItems}
                </DropdownList>
            )}
        </AutocompleteContainer>
    );
};

export default CategoriesList;